import { type ReactNode, useEffect, useState } from "react";
import { Link } from "react-router";

import { ArrowRight, ExternalLink, Loader2 } from "lucide-react";

import { EventSourcesList } from "@/components/common/event-sources";
import { GcsImage } from "@/components/media/gcs-image";
import { ZoomablePhoto } from "@/components/media/photo-zoomable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  REFRESH_CHANGE_KIND_LABEL,
  REFRESH_MODE_HINT,
  REFRESH_MODE_LABEL,
  REFRESH_PAGE_GONE_ACTIONS,
  REFRESH_PAGE_GONE_ACTION_HINT,
  REFRESH_PAGE_GONE_ACTION_LABEL,
  moodEventHref,
  readRefreshPublishedSummary,
  refreshDateComparison,
  refreshPageGoneReasonHint,
  refreshPageGoneReasonLabel,
  refreshProblemOf,
  refreshPushEffect,
  refreshQuietDateNotice,
} from "@/lib/tina/refresh-jobs";
import { cn } from "@/lib/utils";
import { useGeodataRepository } from "@/pages/geodata/hooks/use-geodata-repository";
import {
  type CityCenter,
  readCityCenterById,
} from "@/pages/tina/items/components/panels/hitl-panel/city-center";
import { hitlQueueReturnTo } from "@/pages/tina/items/components/panels/hitl-panel/queue-return";
import {
  type LineupDelta,
  type ProposalDecision,
  type ProposalDecisionMap,
  buildRefreshDecisions,
  canSubmitRefreshResolution,
  countUnanswered,
  decisionAt,
  lineupDeltaKey,
  lineupDeltas,
  lineupTickAt,
  otherSightings,
  percent,
  posterCandidatePosition,
  venueMapsSearchUrl,
} from "@/pages/tina/items/components/panels/hitl-panel/refresh-resolution.helpers";
import type { ResolutionComponentProps } from "@/pages/tina/items/components/panels/hitl-panel/resolution-components";
import { VenueActionFields } from "@/pages/tina/items/components/panels/hitl-panel/venue-action-fields";
import { useItemsRepository } from "@/pages/tina/items/hooks/use-items-repository";
import { useVenueRepository } from "@/pages/venues/hooks/use-venue-repository";
import type {
  PipelineItem,
  ProposedChange,
  RefreshAppliedChange,
  RefreshDuplicateAction,
  RefreshLineupDiff,
  RefreshMode,
  RefreshMoveDuplicate,
  RefreshPageGoneAction,
  RefreshReviewProblem,
} from "@/types/tina";

// The three-strike rule, spelled out for when tina doesn't stamp a specific
// `page_gone_reason` (older parks, or a path that sets `page_gone` without a
// reason string) — mirrors `RefreshReviewProblem.page_gone`'s doc comment.
const PAGE_GONE_FALLBACK_REASON =
  "Three consecutive bad fetches, three nights unlisted on a carry site, or a compare that said the page is now about a different event.";

/**
 * Said out loud, because the alternative is silence (owner ruling R76).
 *
 * `problem.image_refs` carries `gs://` refs and nothing else — no source URLs —
 * so `GcsImage` has no `fallbackUrl` to fall back to, and a failed artifact
 * fetch rendered *nothing*. A curator could not tell "the page offered no new
 * poster" from "the page offered one and we cannot show it to you", which are
 * opposite answers to the only question a poster proposal asks. The 403 behind
 * the current failures is a Tina-orchestrator bug fixed separately (R75); this
 * is only about naming the state while it happens.
 */
const IMAGE_UNAVAILABLE = "Candidate image could not be loaded";

export function RefreshResolution({
  item,
  hitlContext,
  onResolved,
}: ResolutionComponentProps) {
  const { resolveHitl } = useItemsRepository();
  const { search } = useVenueRepository();
  const {
    search: searchGeodata,
    searchNearest,
    get: getGeodata,
  } = useGeodataRepository();

  const problem = refreshProblemOf(hitlContext);
  // The published event's own dump, read once and threaded down to the two rows that
  // need a piece of it. Both come off the same reader rather than off raw JSON, so a
  // field that moves in tina's `PublishedEventView` moves in one place.
  const published = readRefreshPublishedSummary(hitlContext);
  // `timezone` goes to the `date` proposal row, which is the one place a curator has to
  // compare two timestamps written in two different offset conventions.
  // `RefreshContextView` prints this zone one panel up; the rows below never had it, so
  // they had nothing to normalise into and printed raw strings. See `refreshDateComparison`.
  const publishedTimezone = published?.timezone ?? null;
  // `maps_url` goes to a `venue` proposal whose event has no venue record: without it
  // that row is the one place on the panel saying "(empty)" about an event that does
  // carry a location. See `VenueComparison`.
  const publishedMapsUrl = published?.maps_url ?? null;

  const [decisions, setDecisions] = useState<ProposalDecisionMap>({});
  // Retire is the default the moment a page-gone park opens (owner ruling 2026-09-26,
  // after an audit found 62 of 99 `cancel` decisions since 2026-09-23 hit LIVE events) —
  // a curator who submits without touching this section retires rather than cancels.
  // `problem` is read once here: this initializer runs only on mount, which is exactly
  // when a fresh park should get its default.
  const [pageGoneAction, setPageGoneAction] =
    useState<RefreshPageGoneAction | null>(
      problem?.page_gone ? "retire" : null,
    );
  // No default: unlike page-gone, nothing is safe to assume about which of two events
  // survives, so Submit stays disabled until the curator answers (ruling R10).
  const [duplicateAction, setDuplicateAction] =
    useState<RefreshDuplicateAction | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // The city behind the `venue` proposal's "use this city's center" autofill.
  //
  // NOT `published.venue_city`: that is read off the venue's geodata, and a venue
  // proposal is raised precisely because the published event has no venue — so it is
  // null on all 71 open venue proposals (2026-09-11) and the button never appeared.
  // The pin is the field that is always there (71 of 71), so the nearest CITY record
  // to it is the city. Two calls: `searchNearest` says WHICH city, and only the
  // single-record read says where its centre is — see `city-center.ts`.
  const [cityCenter, setCityCenter] = useState<CityCenter | undefined>();
  const venuePin = problem?.proposals?.some((p) => p.kind === "venue")
    ? published?.geolocation
    : null;
  const pinLat = venuePin?.lat;
  const pinLng = venuePin?.lng;
  useEffect(() => {
    if (pinLat === undefined || pinLng === undefined) return;
    let active = true;
    searchNearest({ lat: pinLat, lng: pinLng })
      .then(async ([nearest]) =>
        nearest ? readCityCenterById(getGeodata, nearest) : undefined,
      )
      .then((center) => active && setCityCenter(center))
      .catch(() => active && setCityCenter(undefined));
    return () => {
      active = false;
    };
    // `searchNearest` is stable for these coords; refetch only when they change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinLat, pinLng]);

  // The registry only ever mounts this component for `hitl_reason ===
  // "refresh_review"`, so this is a defensive fallback for a mismatched
  // context rather than a path a curator can actually hit.
  if (!problem) {
    return (
      <p className="text-sm text-muted-foreground">
        This HITL context is not a refresh review — nothing to resolve here.
      </p>
    );
  }

  const proposals = problem.proposals;
  const unanswered = countUnanswered(proposals, decisions);
  const canSubmit = canSubmitRefreshResolution(
    problem,
    decisions,
    pageGoneAction,
    duplicateAction,
  );

  function setDecision(index: number, partial: Partial<ProposalDecision>) {
    setDecisions((prev) => ({
      ...prev,
      [index]: { ...decisionAt(prev, index), ...partial },
    }));
  }

  // Takes the problem rather than closing over it: this is a hoisted function
  // declaration, so it sits outside the `if (!problem)` guard's control-flow
  // narrowing and would see `RefreshReviewProblem | null`. Both call sites are
  // in JSX below the guard, where it is already non-null.
  async function handleSubmit(review: RefreshReviewProblem) {
    setSubmitting(true);
    try {
      await resolveHitl(
        item.id,
        {
          resolution: {
            kind: "refresh.review",
            // The whole problem, not just `proposals`: the per-name lineup list
            // is built from `review.lineup_diff`, and it has to be built from
            // the diff rather than from the ticks — see `buildRefreshDecisions`.
            decisions: buildRefreshDecisions(review, decisions),
            ...(pageGoneAction ? { page_gone_action: pageGoneAction } : {}),
            ...(duplicateAction ? { duplicate_action: duplicateAction } : {}),
          },
        },
        // A refresh park was never in the shared HITL queue — `/tina/hitl` hides
        // the whole class by ruling R55 — so `resolveHitl`'s default would drop
        // the curator into a list their item has never appeared in, at page 1.
        // `hitlQueueReturnTo` gives back `/tina/refresh-review` with the page and
        // filters they left it on.
        { navigateTo: hitlQueueReturnTo(hitlContext) },
      );
      onResolved();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <RefreshHeader item={item} problem={problem} />

      <AutoAppliedSection
        problem={problem}
        eventId={item.id}
        publishedTimezone={publishedTimezone}
      />

      {problem.page_gone && (
        <>
          <Separator />
          <PageGoneSection
            problem={problem}
            selected={pageGoneAction}
            onSelect={setPageGoneAction}
          />
        </>
      )}

      {problem.duplicate_of && (
        <>
          <Separator />
          <DuplicateSection
            problem={problem}
            duplicate={problem.duplicate_of}
            thisName={published?.name}
            selected={duplicateAction}
            onSelect={setDuplicateAction}
          />
        </>
      )}

      {proposals.length > 0 && (
        <>
          <Separator />
          <ProposalsSection
            proposals={proposals}
            mode={problem.mode}
            decisions={decisions}
            onChange={setDecision}
            search={search}
            searchGeodata={searchGeodata}
            unanswered={unanswered}
            eventId={item.id}
            imageRefs={problem.image_refs}
            currentPosterUrl={problem.current_poster_url}
            lineupDiff={problem.lineup_diff}
            publishedTimezone={publishedTimezone}
            publishedMapsUrl={publishedMapsUrl}
            cityCenter={cityCenter}
          />
        </>
      )}

      <div className="flex flex-col gap-1">
        {/* `cancel` already went through its own confirm at the moment it was chosen
            (see `PageGoneSection`), so Submit itself needs no second one — only the
            same destructive colour the action button carries, as a reminder of what
            it is about to post. */}
        <Button
          type="button"
          variant={pageGoneAction === "cancel" ? "destructive" : "default"}
          disabled={!canSubmit || submitting}
          onClick={() => handleSubmit(problem)}
        >
          {submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
          Submit
        </Button>
      </div>
    </div>
  );
}

/**
 * Why this park exists, and what else has seen the event — nothing more (owner ruling
 * R76).
 *
 * ## What this used to say twice
 *
 * The owner's report on a live park was "confusing, cluttered, duplicated", and this
 * header held three of the duplicates:
 *
 * - **The published Mood event, linked twice.** `RefreshContextView` renders directly
 *   above this and already links the event *by name* in its "Event" row, which is both a
 *   better link text and the answer to "which event is this". A second
 *   "Open the published Mood event" line to the identical `moodEventHref` was one
 *   affordance wearing two labels. The context view owns it now, and with it the
 *   `problem.ims_url` warning that used to be repeated here.
 * - **The source URL, a third time.** See `refresh-context.tsx`'s docstring: the item
 *   page header already links `events.url`, and `problem.source_url` is that same column
 *   verbatim. Both panel copies are gone.
 * - **The duplicate explanation, twice in a row.** `REFRESH_MODE_HINT.dup` says "Another
 *   site's page was caught as a duplicate of this published event, and carried different
 *   data"; the paragraph under it said the same sentence again with different articles.
 *   The hint keeps the sentence — it is the mode badge's whole job, and `scheduled` needs
 *   its own — and the line below keeps only what the hint cannot carry: which Tina item
 *   that other page is, and how sure the dedup was.
 */
function RefreshHeader({
  item,
  problem,
}: {
  item: PipelineItem;
  problem: RefreshReviewProblem;
}) {
  const hasPosterProposal = problem.proposals.some(
    (proposal) => proposal.kind === "poster",
  );
  // The page under review is not a second opinion about itself — see
  // `otherSightings`. Filtered here rather than inside `EventSourcesList`
  // because the event edit page, its other caller, has no page under review.
  const otherSources = otherSightings(problem.sources, problem.source_url);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge className="bg-indigo-500/15 text-indigo-700 dark:text-indigo-300">
          {REFRESH_MODE_LABEL[problem.mode]}
        </Badge>
        <span className="text-xs text-muted-foreground">
          {REFRESH_MODE_HINT[problem.mode]}
        </span>
      </div>

      {problem.mode === "dup" && problem.dup_source_event_id && (
        <p className="text-sm text-muted-foreground">
          Duplicate item{" "}
          <Link
            to={`/tina/items/show/${problem.dup_source_event_id}`}
            className="font-mono text-[13px] text-blue-500 hover:underline"
          >
            {problem.dup_source_event_id}
          </Link>
          {typeof problem.dup_confidence === "number" && (
            <> · {percent(problem.dup_confidence)} confidence</>
          )}
        </p>
      )}

      {/* Under the dup context, and rendered in both modes: the question a
          curator asks before accepting a change is "what else has said this",
          and the answer is the same list the event edit page shows — minus the
          page being reviewed, which is not an "else". Renders nothing for a park
          with no other source recorded, including every park written before R30
          shipped. */}
      <EventSourcesList sources={otherSources} />

      {problem.blanks.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">
            Still blank on the published event — tina fills these in on its own,
            so they are notes, not decisions:
          </span>
          <div className="flex flex-wrap gap-1">
            {problem.blanks.map((kind) => (
              <span
                key={kind}
                className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
              >
                {REFRESH_CHANGE_KIND_LABEL[kind]}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Only when no poster proposal owns them (owner ruling R36). A poster
          proposal renders the same candidates beside the current poster, and
          two copies of one image on a single screen reads as two candidates.
          The strip still matters without one: a page_gone park, or an
          auto-filled poster, carries candidates with nothing to decide. */}
      {problem.image_refs.length > 0 && !hasPosterProposal && (
        <div className="flex flex-wrap gap-2">
          {problem.image_refs.map((ref) => (
            // GcsImage proxies through the orchestrator rather than
            // hotlinking: poster hosts like Instagram send
            // `Cross-Origin-Resource-Policy: same-origin`, which breaks a
            // plain <img src>. `fallback` covers the other half of that:
            // the proxy itself can fail, and a strip that silently shrinks
            // is indistinguishable from a park with no candidates.
            <GcsImage
              key={ref}
              eventId={item.id}
              artifactRef={ref}
              alt="New poster candidate"
              className="h-20 w-20 rounded-md border object-cover"
              fallback={
                <ImageNotice className="h-20 w-20">
                  {IMAGE_UNAVAILABLE}
                </ImageNotice>
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * What tina already wrote to this event before parking, before and after (2026-09-11).
 *
 * `proposals` is strictly what is still OPEN (owner ruling R70), so without this a curator
 * looking at a park could see a moved description or a new poster on the live event and
 * nothing on the panel saying why. `auto_applied_changes` carries the before/after;
 * `auto_applied` alone (a park whose write predates the record) degrades to the bare kind
 * chips. Renders nothing when the job wrote nothing.
 *
 * A `poster` row renders as pictures rather than as its two strings — see
 * {@link AppliedPosterComparison}. A `ticket_url` row that carried a price also prints it
 * ({@link TicketPrice}): a price move is auto-applied at high confidence, so it never
 * parks, and the applied record is the ONLY place the change appears — where `<URL> → <URL>`
 * used to be the whole of it.
 */
function AutoAppliedSection({
  problem,
  eventId,
  publishedTimezone,
}: {
  problem: RefreshReviewProblem;
  eventId: string;
  publishedTimezone: string | null;
}) {
  const changes: RefreshAppliedChange[] = problem.auto_applied_changes ?? [];
  const kindsOnly = (problem.auto_applied ?? []).filter(
    (kind) => !changes.some((change) => change.kind === kind),
  );
  if (changes.length === 0 && kindsOnly.length === 0) return null;
  return (
    <>
      <Separator />
      <div className="flex flex-col gap-2" data-testid="auto-applied">
        <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
          Already applied automatically
        </h3>
        <p className="text-xs text-muted-foreground">
          Tina wrote these to the published event before parking the rest for
          you — they are live already and need no decision here.
        </p>
        {changes.map((change, index) => (
          <div
            key={`${change.kind}-${index}`}
            className="flex flex-col gap-1 rounded-md border border-dashed p-3"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-medium text-foreground">
                {REFRESH_CHANGE_KIND_LABEL[change.kind]}
              </span>
              <span className="text-xs text-muted-foreground">
                {percent(change.confidence)} confidence · applied
              </span>
            </div>
            {change.kind === "poster" ? (
              <AppliedPosterComparison
                eventId={eventId}
                change={change}
                imageRefs={problem.image_refs}
              />
            ) : change.kind === "date" ? (
              // The second kind whose before/after is not plain text. An applied
              // `date` carries the same two-offset-conventions problem a `date`
              // PROPOSAL does — and it was printing both sides raw, one section
              // above the proposal rows that normalise them. Same component, same
              // zone, so a write that already happened and one still awaiting a
              // decision read alike.
              <DateComparison
                proposal={{ old: change.old ?? null, new: change.new }}
                timezone={publishedTimezone}
              />
            ) : (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-muted-foreground">
                  {change.old ?? "(empty)"}
                </span>
                <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="font-medium text-foreground">
                  {change.new}
                </span>
              </div>
            )}
            {/* The price this write carried, when it carried one (MOO-4822). Outside the
                ternary above, so a ticket record without a price renders byte-for-byte
                what it rendered before the pair existed — `poster` and `date` cannot
                carry one, and a `ticket_url` change whose page stated none is the
                ordinary case. */}
            {change.kind === "ticket_url" && change.price != null && (
              <TicketPrice
                label={WRITTEN_LINK_PRICE_LABEL}
                price={change.price}
                currency={change.currency}
              />
            )}
            {change.evidence && (
              <blockquote className="border-l-2 pl-3 text-xs italic text-muted-foreground">
                “{change.evidence}”
              </blockquote>
            )}
          </div>
        ))}
        {kindsOnly.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {kindsOnly.map((kind) => (
              <span
                key={kind}
                className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
              >
                {REFRESH_CHANGE_KIND_LABEL[kind]}
              </span>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/**
 * The two posters an automatic write swapped, as pictures (2026-09-11).
 *
 * A `poster` change is the one kind whose before/after is not text. Printing its two
 * strings was legible for a description and useless here: a curator read
 * `https://cdn…/img-5426681e.webp → candidate 1` and could see neither image, which is
 * the same complaint {@link PosterComparison} answers one section below for a poster
 * *proposal*. This is that comparison for a write that already happened — the art the
 * event carried, then the art that replaced it.
 *
 * The halves are fetched differently for the same reason they are on a proposal. The old
 * poster is already published off our own CDN, so `ZoomablePhoto` takes the URL directly
 * and needs no proxy; the replacement is a scraped artifact whose host may send
 * `Cross-Origin-Resource-Policy: same-origin`, so it resolves the recorded 1-based
 * position against `image_refs` and goes through `GcsImage`'s orchestrator proxy — the
 * same index `_poster_media` read when it wrote.
 *
 * A missing half keeps its label and says why in words, as on a proposal: "the previous
 * art was a placeholder" and "we did not store the image we wrote" are different answers,
 * and a bare gap reads as whichever one the curator feared.
 */
function AppliedPosterComparison({
  eventId,
  change,
  imageRefs,
}: {
  eventId: string;
  change: RefreshAppliedChange;
  imageRefs: string[];
}) {
  const [oldFailed, setOldFailed] = useState(false);
  const position = posterCandidatePosition(change.new);
  const replacement = position === null ? undefined : imageRefs[position - 1];
  return (
    <div className="flex flex-wrap items-start gap-4">
      <figure className="flex flex-col gap-1">
        <PosterLabel>Was</PosterLabel>
        {change.old && !oldFailed ? (
          <ZoomablePhoto
            url={change.old}
            alt="Previous event poster"
            className="h-28 w-28 rounded-md border object-cover"
            onError={() => setOldFailed(true)}
          />
        ) : (
          <ImageNotice>Placeholder art</ImageNotice>
        )}
      </figure>
      <figure className="flex flex-col gap-1">
        <PosterLabel>Now</PosterLabel>
        {replacement ? (
          <GcsImage
            eventId={eventId}
            artifactRef={replacement}
            alt="Poster applied automatically"
            className="h-28 w-28 rounded-md border object-cover"
            fallback={<ImageNotice>{IMAGE_UNAVAILABLE}</ImageNotice>}
          />
        ) : (
          <ImageNotice>Replaced image not stored</ImageNotice>
        )}
      </figure>
    </div>
  );
}

function PageGoneSection({
  problem,
  selected,
  onSelect,
}: {
  problem: RefreshReviewProblem;
  selected: RefreshPageGoneAction | null;
  onSelect: (action: RefreshPageGoneAction) => void;
}) {
  const pageGoneHint = refreshPageGoneReasonHint(problem.page_gone_reason);
  // Cancel is offered on every reason, `blocked`/`unreadable`/`redirected` included:
  // the 2026-09-26 rule that hid it there was reversed on 2026-09-29 (owner), who
  // briefed curators instead. The reason hint and the confirm dialog stay the guard.
  const actions = REFRESH_PAGE_GONE_ACTIONS;

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
        Page gone
      </h3>
      {/* `page_gone_reason` is a code (`focal_missing`, `bad_fetch`, …), not prose:
          printed raw it read as a stack trace where the explanation belongs, and the
          answer below genuinely differs per code — a bad-fetch streak is usually an
          outage worth dismissing, "the page is about a different event now" is not.
          The raw code stays on hover, because it is what a curator greps tina's logs
          for. */}
      <p className="text-sm">
        <span
          className="font-medium"
          title={problem.page_gone_reason ?? undefined}
        >
          {refreshPageGoneReasonLabel(problem.page_gone_reason) ??
            PAGE_GONE_FALLBACK_REASON}
        </span>
        {/* Optional, and absent on most parks today (2026-09-26): a park not raised
            off the fetch stage's strike counter never carries one. */}
        {typeof problem.page_gone_strikes === "number" && (
          <span className="text-muted-foreground">
            {" "}
            · {problem.page_gone_strikes}{" "}
            {problem.page_gone_strikes === 1 ? "bad night" : "bad nights"}
          </span>
        )}
        {pageGoneHint && (
          <span className="text-muted-foreground"> — {pageGoneHint}</span>
        )}
      </p>
      <div className="flex flex-col gap-2">
        {actions.map((action) => {
          const variant =
            selected === action
              ? action === "cancel"
                ? "destructive"
                : "default"
              : "outline";
          const label = (
            <span className="flex flex-col items-start gap-0.5 whitespace-normal text-left">
              <span className="font-medium">
                {REFRESH_PAGE_GONE_ACTION_LABEL[action]}
              </span>
              <span className="text-xs font-normal opacity-80">
                {REFRESH_PAGE_GONE_ACTION_HINT[action]}
              </span>
            </span>
          );

          // Cancel pushes EVENT_CANCELLED to everyone who saved the event — an
          // audit found 62 of 99 cancels since 2026-09-23 hit LIVE events. So,
          // unlike Retire and Dismiss, choosing it gates behind its own confirm
          // right here, at the moment of choice — not only at Submit, where a
          // curator who never meant to pick it would already see the state flipped.
          if (action !== "cancel") {
            return (
              <Button
                key={action}
                type="button"
                variant={variant}
                className="h-auto justify-start py-2"
                onClick={() => onSelect(action)}
              >
                {label}
              </Button>
            );
          }

          return (
            <Dialog key={action}>
              <DialogTrigger asChild>
                <Button
                  type="button"
                  variant={variant}
                  className="h-auto justify-start py-2"
                >
                  {label}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Cancel this event?</DialogTitle>
                  <DialogDescription>
                    This sends a cancellation push to everyone who saved this
                    event. Use it only when the evidence says the page is really
                    gone for good.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button type="button" variant="outline">
                      Go back
                    </Button>
                  </DialogClose>
                  <DialogClose asChild>
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => onSelect("cancel")}
                    >
                      Yes, cancel it
                    </Button>
                  </DialogClose>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          );
        })}
      </div>
    </div>
  );
}

const DUPLICATE_ACTIONS: { action: RefreshDuplicateAction; label: string }[] = [
  { action: "keep_this", label: "Keep this one" },
  { action: "keep_other", label: "Keep the other one" },
  { action: "not_duplicate", label: "Not a duplicate" },
];

function DuplicateEventCard({
  title,
  name,
  detail,
  adminEventId,
}: {
  title: string;
  name: string;
  detail: string;
  adminEventId: string;
}) {
  const href = moodEventHref(adminEventId);
  return (
    <div className="flex flex-1 flex-col gap-0.5 rounded-md border p-2 text-sm">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">
        {title}
      </span>
      <span className="font-medium">{name}</span>
      {detail && <span className="text-muted-foreground">{detail}</span>}
      {href && (
        <Link
          to={href}
          className="inline-flex items-center gap-1 text-blue-500 hover:underline"
        >
          Open in IMS <ExternalLink className="h-3 w-3" />
        </Link>
      )}
    </div>
  );
}

/**
 * Asked after a venue or date move landed this event on another published one. On
 * either keep Tina re-points source links, stops refreshing the loser and deletes it
 * (behind Tina's TINA_REFRESH_MOVE_DUP_DELETE_LOSER_ENABLED). It keeps a loser users
 * saved, so the link to the loser stays for that case. `moodEventHref`, never `ims_url`.
 */
function DuplicateSection({
  problem,
  duplicate,
  thisName,
  selected,
  onSelect,
}: {
  problem: RefreshReviewProblem;
  duplicate: RefreshMoveDuplicate;
  /** Published event's name only: its venue and start are from before the move. */
  thisName: string | null | undefined;
  selected: RefreshDuplicateAction | null;
  onSelect: (action: RefreshDuplicateAction) => void;
}) {
  const loser =
    selected === "keep_this"
      ? { id: duplicate.admin_event_id, which: "the other" }
      : selected === "keep_other"
        ? { id: problem.admin_event_id, which: "this" }
        : null;
  const loserHref = loser ? moodEventHref(loser.id) : null;

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
        Possible duplicate after this move
      </h3>
      <div className="flex flex-col gap-2 sm:flex-row">
        <DuplicateEventCard
          title="This event"
          name={thisName || problem.admin_event_id}
          detail=""
          adminEventId={problem.admin_event_id}
        />
        <DuplicateEventCard
          title="Other event"
          name={duplicate.name}
          detail={[duplicate.venue_name, duplicate.start]
            .filter(Boolean)
            .join(" · ")}
          adminEventId={duplicate.admin_event_id}
        />
      </div>
      <p className="text-sm text-muted-foreground">
        {duplicate.reasoning} ({Math.round(duplicate.confidence * 100)}% sure)
      </p>
      <div className="flex flex-wrap gap-2">
        {DUPLICATE_ACTIONS.map(({ action, label }) => (
          <Button
            key={action}
            type="button"
            variant={selected === action ? "default" : "outline"}
            onClick={() => onSelect(action)}
          >
            {label}
          </Button>
        ))}
      </div>
      {loser && loserHref && (
        <p className="text-sm">
          Tina deletes {loser.which} event after Submit. If users saved it, Tina
          keeps it, so delete it by hand:{" "}
          <Link
            to={loserHref}
            className="inline-flex items-center gap-1 text-blue-500 hover:underline"
          >
            Open the event to delete <ExternalLink className="h-3 w-3" />
          </Link>
        </p>
      )}
    </div>
  );
}

interface PosterEvidence {
  /** The Tina job row, which scopes the artifact fetch for a candidate ref. */
  eventId: string;
  /** `gs://` refs of the candidates the page offered. */
  imageRefs: string[];
  /** The published event's current poster, or null/absent when it has none. */
  currentPosterUrl?: string | null;
}

function ProposalsSection({
  proposals,
  mode,
  decisions,
  onChange,
  search,
  searchGeodata,
  unanswered,
  eventId,
  imageRefs,
  currentPosterUrl,
  lineupDiff,
  publishedTimezone,
  publishedMapsUrl,
  cityCenter,
}: {
  proposals: ProposedChange[];
  /**
   * The park's refresh mode. Read by the `ticket_url` row, which says two
   * different things depending on it — see `TicketLinkAppend`. Threaded rather
   * than read from a context, like every other piece of panel context here.
   */
  mode: RefreshMode;
  decisions: ProposalDecisionMap;
  onChange: (index: number, partial: Partial<ProposalDecision>) => void;
  search: (query: string) => Promise<unknown[]>;
  /** Geodata search for the two Maps-link venue actions — see `VenueActionFields`. */
  searchGeodata: (query: string) => Promise<unknown[]>;
  unanswered: number;
  /** The park's per-performer diff, or null/absent — see `LineupDeltaList`. */
  lineupDiff?: RefreshLineupDiff | null;
  /** The published event's IANA zone, for the `date` row's one frame — see `DateComparison`. */
  publishedTimezone: string | null;
  /**
   * The published event's own map link, for a `venue` proposal whose event has no
   * venue record — see `VenueComparison`. Null when the event carries no pin, which
   * leaves that row's "(empty)" true and in place.
   */
  publishedMapsUrl: string | null;
  /** The city the autofill button offers — see `RefreshResolution`'s `searchNearest` effect. */
  cityCenter?: CityCenter;
} & PosterEvidence) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
          Proposed changes
        </h3>
        <span
          className={cn(
            "text-xs",
            unanswered > 0
              ? "font-medium text-amber-600 dark:text-amber-400"
              : "text-muted-foreground",
          )}
        >
          {unanswered > 0
            ? `${unanswered} of ${proposals.length} still need a decision`
            : "All decided"}
        </span>
      </div>
      {proposals.map((proposal, index) => (
        <ProposalRow
          // Index, not kind: a payload is not guaranteed to carry each kind
          // only once, and the decision map itself is keyed by index.
          key={index}
          proposal={proposal}
          mode={mode}
          decision={decisionAt(decisions, index)}
          onChange={(partial) => onChange(index, partial)}
          search={search}
          searchGeodata={searchGeodata}
          eventId={eventId}
          imageRefs={imageRefs}
          currentPosterUrl={currentPosterUrl}
          lineupDiff={lineupDiff}
          publishedTimezone={publishedTimezone}
          publishedMapsUrl={publishedMapsUrl}
          cityCenter={cityCenter}
        />
      ))}
    </div>
  );
}

function ProposalRow({
  proposal,
  mode,
  decision,
  onChange,
  search,
  searchGeodata,
  eventId,
  imageRefs,
  currentPosterUrl,
  lineupDiff,
  publishedTimezone,
  publishedMapsUrl,
  cityCenter,
}: {
  proposal: ProposedChange;
  mode: RefreshMode;
  decision: ProposalDecision;
  onChange: (partial: Partial<ProposalDecision>) => void;
  search: (query: string) => Promise<unknown[]>;
  searchGeodata: (query: string) => Promise<unknown[]>;
  lineupDiff?: RefreshLineupDiff | null;
  publishedTimezone: string | null;
  /** The published event's own map link — see `ProposalsSection`. */
  publishedMapsUrl: string | null;
  /** The city the autofill button offers — see `RefreshResolution`'s `searchNearest` effect. */
  cityCenter?: CityCenter;
} & PosterEvidence) {
  const isVenue = proposal.kind === "venue";
  const isPoster = proposal.kind === "poster";
  const isDate = proposal.kind === "date";
  // A `ticket_url` proposal on a DUPLICATE job is the fourth kind that must not
  // wear the old→new arrow — see `TicketLinkAppend`.
  const isTicketAppend = proposal.kind === "ticket_url" && mode === "dup";
  // The fifth, and the only one where ONE HALF of the line is the lie rather than the
  // whole line — see `VenueComparison`. "No usable old value" rather than a bare
  // `!proposal.old`: the wire can carry an empty or all-whitespace string, which is a
  // blank where a value belongs — the same defect `(empty)` was invented for, one step
  // quieter.
  const hasOldValue =
    typeof proposal.old === "string" && proposal.old.trim() !== "";
  const isVenueLocation = isVenue && !hasOldValue && Boolean(publishedMapsUrl);
  // Not `REFRESH_KIND_NOTIFICATION[proposal.kind]`: for a `date` proposal the kind
  // is only half the answer since owner ruling R79, and this row is where the
  // promise gets made. See `refreshPushEffect`.
  const pushEffect = refreshPushEffect(proposal);

  // Only a `lineup` proposal has deltas, and only when the park carries a diff.
  // `[]` here is the degradation switch for the whole row: no deltas means the
  // old→new JSON line, the plain Accept/Reject pair and the free-text value
  // input, i.e. byte-for-byte what this panel did before the per-name path.
  const deltas =
    proposal.kind === "lineup"
      ? lineupDeltas(lineupDiff)
      : ([] as LineupDelta[]);
  const perName = deltas.length > 0;
  // The candidate the model named, if `new` is a position at all — see
  // `posterCandidatePosition`.
  const proposedPosition = isPoster
    ? posterCandidatePosition(proposal.new)
    : null;

  return (
    <div className="flex flex-col gap-2 rounded-md border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium text-foreground">
          {REFRESH_CHANGE_KIND_LABEL[proposal.kind]}
        </span>
        <span className="text-xs text-muted-foreground">
          {percent(proposal.confidence)} confidence
        </span>
      </div>

      {/* Suppressed or replaced for five cases, for the same reason in all five:
          this line is a worse rendering of something truer than it can carry.

          A lineup proposal's `old`/`new` are raw JSON arrays and the tick list
          is the same roster made readable and actionable.

          A poster proposal's are not values at all (owner ruling R76). The live
          park carried `old: null, new: "1"`, so this printed "(empty) → 1" for
          an event that HAS a poster and a model whose reasoning was about
          replacing it: `old` is empty because a poster URL is not what the
          compare stage diffs, and `new` is the candidate POSITION. Both halves
          were false. `PosterComparison` below shows the actual images with the
          named candidate marked, which is the only form of this proposal a
          curator can judge.

          A date proposal's two sides are both real values, and both true — but
          written in two different offset conventions, so read side by side they
          are not a comparison at all (owner ruling R80). `DateComparison` puts
          them on one clock and keeps these same raw strings underneath.

          A `ticket_url` proposal on a DUPLICATE job — the fourth, and the first
          one the MODE decides rather than the kind. The two sides are both true
          values in one frame, so the values are not the problem; the ARROW is.
          The arrow promises a swap, and on a dup job accepting the proposal
          leaves the published link where it is and appends the proposed one, so
          the event ends up with two places to buy. `TicketLinkAppend` says that
          instead. On a `scheduled` job the arrow stays: that path really does
          put the proposed link in the published one's place.

          A `venue` proposal with nothing to compare against — the fifth. Unlike
          the other four, the line is not wrong in both halves here, and the arrow
          between them is true: the event really is at one location and the page
          proposes another. The lie is the single word "(empty)". The published
          event has no venue RECORD, so `old` is null — but it does have a Google
          Maps pin, so it has a location the curator could compare against, and
          this line hides it while asking "is this the right venue?". The one piece
          of evidence that answers the question was the piece not shown.
          `VenueComparison` prints the pin in that half's place. When there is no
          pin either, "(empty)" stays: it is honest then, and this line is the
          whole truth there is. */}
      {!perName &&
        !isPoster &&
        !isDate &&
        !isTicketAppend &&
        !isVenueLocation && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground">
              {proposal.old ?? "(empty)"}
            </span>
            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="font-medium text-foreground">{proposal.new}</span>
          </div>
        )}

      {isVenueLocation && publishedMapsUrl && (
        <VenueComparison proposal={proposal} mapsUrl={publishedMapsUrl} />
      )}

      {isVenue && (
        <VenueMapsSearch venueText={proposal.new} city={cityCenter?.name} />
      )}

      {isDate && (
        <DateComparison
          proposal={proposal}
          timezone={publishedTimezone}
          // Tina writes a takeover's `new_end` only when the start is accepted as
          // proposed; an edited start shifts the published end with it instead.
          startEdited={
            decision.accept === true &&
            decision.editedValue !== undefined &&
            decision.editedValue !== proposal.new
          }
        />
      )}

      {isTicketAppend && <TicketLinkAppend proposal={proposal} />}

      {/* A `ticket_url` proposal whose only real difference is the price renders as
          the same URL on both sides of the line above, so the row would ask a
          curator to approve a change it does not show. The proposal carries the
          price and its currency beside the URL for exactly this, and neither is
          sent on accept or reject — this is the curator's evidence, like the
          page quote below it.

          Not on the `dup` path's `TicketLinkAppend` half alone: a price read off a
          second source's page is the same fact there, and the row is the same row.
          On that path it is also the row's whole ambiguity — the line sits under TWO
          links there, and `price` is the PROPOSED link's, so the label has to say so.
          See `TicketPrice`. */}
      {proposal.kind === "ticket_url" && proposal.price != null && (
        <TicketPrice
          label={PROPOSED_LINK_PRICE_LABEL}
          price={proposal.price}
          currency={proposal.currency}
        />
      )}

      {/* The verbatim page quote — the only thing that lets a curator check
          the model without opening the source themselves. */}
      <blockquote className="border-l-2 pl-3 text-xs italic text-muted-foreground">
        “{proposal.evidence}”
      </blockquote>

      {perName && (
        <LineupDeltaList
          deltas={deltas}
          unchanged={lineupDiff?.unchanged ?? []}
          decision={decision}
          onChange={onChange}
        />
      )}

      {isPoster && (
        <PosterComparison
          eventId={eventId}
          imageRefs={imageRefs}
          currentPosterUrl={currentPosterUrl}
          proposedPosition={proposedPosition}
        />
      )}

      {pushEffect.effect === "push" && (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          Accepting this fires{" "}
          <span className="font-mono">{pushEffect.workflow}</span> to everyone
          who saved the event.
        </p>
      )}

      {/* R79's other half. A sub-day date change still applies, so saying
          nothing at all would leave "this pushes" and "this does not push"
          looking identical — and the curator guessing which one they are
          looking at. Muted rather than amber: it is a fact about the change,
          not a warning about its blast radius. */}
      {pushEffect.effect === "quiet" && (
        <p className="text-xs text-muted-foreground">
          {refreshQuietDateNotice(pushEffect.deltaMs)}
        </p>
      )}

      <div className="flex flex-wrap gap-1">
        <Button
          type="button"
          size="sm"
          variant={decision.accept === true ? "default" : "outline"}
          onClick={() => onChange({ accept: true })}
        >
          {/* Named for what it does: with a tick list on screen, a bare
              "Accept" reads as "accept the whole proposal" — which is the one
              thing this button no longer means. */}
          {perName ? "Accept ticked" : "Accept"}
        </Button>
        {perName && (
          // The "the model got it all right" shortcut, and the only way to say
          // yes to a removal without hunting for its checkbox. It ticks every
          // row rather than bypassing the list, so the payload it produces is
          // the same exhaustive list as any other accept — just an all-true
          // one — and the curator can still see and undo what it did.
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() =>
              onChange({
                accept: true,
                lineupTicks: Object.fromEntries(
                  deltas.map((delta) => [lineupDeltaKey(delta), true]),
                ),
              })
            }
          >
            Accept all
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          variant={decision.accept === false ? "destructive" : "outline"}
          onClick={() =>
            // Clear any in-progress edit/venue pick/tick made before switching
            // to Reject, so flipping back to Accept never resurrects a stale
            // value — and in particular never resurrects a removal the curator
            // ticked and then thought better of.
            onChange({
              accept: false,
              editedValue: undefined,
              venueId: undefined,
              venueOption: undefined,
              venueAction: undefined,
              lineupTicks: undefined,
            })
          }
        >
          Reject
        </Button>
      </div>

      {decision.accept === true && isVenue && (
        <div className="flex flex-col gap-1">
          {/* The scrape venue HITL's three ways out, on a refresh row (2026-09-11):
              pick an existing venue, create one from a Google Maps link, or use the
              link as the location with no venue record. `venueAction` is set only
              once the choice is writable, and Submit stays blocked until it is —
              `refresh_apply` cannot write an accepted venue change with nothing
              behind it. A pick also keeps `venueId` for the legacy `value`. */}
          <VenueActionFields
            pageVenueText={proposal.new}
            venueOption={decision.venueOption}
            search={search}
            searchGeodata={searchGeodata}
            cityCenter={cityCenter}
            onChange={(action, option) =>
              onChange({
                venueAction: action,
                venueOption: option,
                venueId:
                  action?.kind === "venue.pick" ? action.venue_id : undefined,
              })
            }
          />
          {!decision.venueAction && !decision.venueId && (
            <span className="text-xs font-medium text-destructive">
              Pick a venue, or paste a Google Maps link, to accept this change.
            </span>
          )}
        </div>
      )}

      {/* `!perName`: with a tick list on screen the roster is already being
          edited row by row, and a second, free-text copy of the same JSON would
          be a second instruction for the same field. `buildRefreshDecisions`
          relies on this — it sends the exhaustive `lineup` list *or* a `value`,
          never both, and this is what makes that true rather than a race. */}
      {decision.accept === true && !isVenue && !perName && (
        <div className="flex flex-col gap-1">
          <Input
            // Named for the screen reader and for the tests: there is no
            // visible label, because the row's own heading already says which
            // change this is.
            aria-label={`${REFRESH_CHANGE_KIND_LABEL[proposal.kind]} value to write`}
            value={decision.editedValue ?? proposal.new}
            onChange={(event) => onChange({ editedValue: event.target.value })}
          />
          {/* Leaving it alone omits `value` entirely, which tells tina to take
              the proposal verbatim; typing anything sends an explicit edit,
              even if it happens to match.

              Poster gets its own sentence because the field is not text at
              all: `refresh_apply` reads a poster decision's value as a 1-based
              index into `image_refs` (`stages/apply.py::_poster_media`), so
              "fix the page's wording" was advice about the wrong thing — this
              box is how a curator says "use candidate 2 instead". Same payload
              either way; only the sentence differs. */}
          <span className="text-xs text-muted-foreground">
            {decision.editedValue !== undefined
              ? "Your edit will be written instead of the proposed value."
              : isPoster
                ? "Accepted as proposed — type another candidate number to use a different one."
                : "Accepted as proposed — edit only if the page's wording needs fixing."}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * A `date` proposal's two sides on one clock, with the gap in words (owner ruling R80).
 *
 * ## What this replaces
 *
 * The generic `old → new` line, which for a `date` proposal was comparing two values that
 * were never in the same frame. Mood stores `startDate` in UTC; `refresh_compare.j2` asks
 * the model for the **page's** local offset. So the owner's trigger park,
 * `2026-09-20T17:30:00+00:00` → `2026-09-20T19:00:00+02:00`, printed as a jump from 17:30 to
 * 19:00 — where the actual change is 19:30 → 19:00 in Europe/Berlin, a 30-minute correction.
 * A real half-hour fix and a pure offset restatement of the same instant rendered identically,
 * and neither rendered as what it was.
 *
 * ## Three lines, in the order a curator reads them
 *
 * 1. **The pair, normalised** — both instants in the event's own zone, so the arrow means
 *    something.
 * 2. **The gap in plain words** plus the frame it was read in. "30 minutes later" is the
 *    sentence; no pair of timestamps competes with it.
 * 3. **The stored strings, monospaced and muted** — kept, because a curator debugging an
 *    offset needs the literal value, and because the normalised line is a *reading* of these
 *    two, not a replacement for them. Secondary, not gone.
 *
 * When either side cannot be placed on a clock, line 1 and the delta are simply not claimed:
 * the note says so, and line 3 carries the whole answer. Guessing the zone would be worse
 * than not normalising — see `refreshDateComparison`, which refuses the browser's own
 * timezone for the reason R79's half of this already documented.
 */
function DateComparison({
  proposal,
  timezone,
  startEdited = false,
}: {
  // The two sides only, never the whole `ProposedChange`: an APPLIED `date` change
  // renders through here too, and `RefreshAppliedChange` is the same two values with
  // an optional `old`. Narrowing the prop is what lets one component serve both
  // rather than a near-copy serving the second. `new_end` is optional and only a
  // takeover proposal carries it, so the applied row simply never passes one.
  proposal: Pick<ProposedChange, "old" | "new" | "new_end">;
  timezone: string | null;
  /** The curator typed a start other than the proposal's. Tina then does not write
   * `new_end`, so the row stops showing an end that will not be written. */
  startEdited?: boolean;
}) {
  const comparison = refreshDateComparison(proposal, timezone);
  const showEnd = !startEdited;

  return (
    <div className="flex flex-col gap-1">
      {comparison.status === "normalised" ? (
        <>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground">{comparison.from}</span>
            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="font-medium text-foreground">{comparison.to}</span>
          </div>
          {/* A takeover's end (`new_end`): accepting the date writes it too, so the
              curator has to see it. Read on the same clock as the start above. */}
          {showEnd && comparison.end && (
            <div className="text-sm text-muted-foreground">
              Ends{" "}
              <span className="font-medium text-foreground">
                {comparison.end}
              </span>
            </div>
          )}
          {showEnd && proposal.new_end && comparison.end === null && (
            <p className="text-xs text-amber-700 dark:text-amber-300">
              The proposed end cannot be read as an exact instant, so it is
              shown only as stored, below.
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            {/* The delta carries the emphasis, not the timestamps: it is the one
                thing on this row that answers "does this matter". */}
            <span className="font-medium text-foreground">
              {comparison.delta}
            </span>{" "}
            · {comparison.frame}
          </p>
        </>
      ) : (
        <div className="flex flex-col gap-0.5">
          {/* Amber, like the push warning: an un-normalised comparison is a state
              the curator has to account for, not a neutral fact about the row. */}
          <p className="text-xs text-amber-700 dark:text-amber-300">
            {comparison.note}
          </p>
          <p className="text-xs text-muted-foreground">{comparison.detail}</p>
        </div>
      )}

      {/* Outside the branch above so it also shows when the start could not be
          normalised: the raw line below hides its end either way. */}
      {startEdited && proposal.new_end && (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          Edited start: the proposed end is not written; the end moves with the
          start.
        </p>
      )}

      {/* Verbatim, and never reformatted. This is what is stored on the Mood event
          and what tina's compare stage emitted, which is what a curator greps for
          and what an offset bug shows up in. */}
      <div className="flex flex-wrap items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
        <span>{proposal.old ?? "(empty)"}</span>
        <span aria-hidden="true">→</span>
        <span>{proposal.new}</span>
        {showEnd && proposal.new_end && <span>· ends {proposal.new_end}</span>}
      </div>
    </div>
  );
}

/**
 * A `ticket_url` proposal on a duplicate job, which ADDS a link rather than swapping one.
 *
 * ## What this replaces
 *
 * The generic `old → new` line, whose arrow promised the proposed link would take the published
 * one's place. On a `dup` job nothing is swapped: the published link stays where it is and the
 * accepted one is appended, so the event ends up with two places to buy. Both values on that
 * line were true — the arrow between them was not, and it was the only thing on the row saying
 * what accepting would do. On a `scheduled` job the arrow is right and stays; the mode is what
 * decides, which is why `ProposalRow` needs it.
 *
 * ## The two sentences, and why each is here
 *
 * 1. **The published link, plus "stays on the event"** — the question a curator asks of a link
 *    they can already see is "is this one going away", and on this job the answer is no.
 * 2. **The proposed link, as "another place to buy"** — what accepting it makes it. Named as an
 *    addition, because that is the whole difference from the `scheduled` case.
 *
 * No arrow anywhere, and the word "replace" is not used: on this job nothing is replaced.
 *
 * A park with no published link at all (possible in principle — tina's auto-fill normally writes
 * a blank `ticket_url` itself rather than parking it) drops the first sentence, which would
 * otherwise claim a link that is not there. The second still holds.
 */
function TicketLinkAppend({ proposal }: { proposal: ProposedChange }) {
  return (
    <div className="flex flex-col gap-0.5 text-sm">
      {proposal.old && (
        <span className="text-muted-foreground">
          {proposal.old} stays on the event.
        </span>
      )}
      <span className="text-foreground">
        {proposal.new} is added as another place to buy.
      </span>
    </div>
  );
}

/**
 * Whose price the line is, on each of the two rows that show one.
 *
 * Two labels and not one, because the two rows ask two different questions: a parked row
 * asks "should I accept this link", so its price is the PROPOSED link's; an applied row
 * asks "what did tina already write", so its price is the link that was written. Calling
 * the applied one "proposed" would point a curator at a link nobody is proposing any more,
 * on a card whose own heading says the change already happened.
 */
const PROPOSED_LINK_PRICE_LABEL = "Ticket price of the proposed link";
const WRITTEN_LINK_PRICE_LABEL = "Ticket price of the new link";

/**
 * The price a ticket change carried, and whose price it is (MOO-4822).
 *
 * One render for the two rows that show it — the parked `ticket_url` proposal and the
 * already-applied one — because it is one fact in two places, and `label` is the only thing
 * that differs between them.
 *
 * The label is not decoration. On a `dup` park the parked row prints TWO links, which makes
 * an unattributed "Ticket price: 30 EUR" readable as the published link's price; on an
 * applied price-only write both halves of the arrow are the SAME url, so the number is the
 * only thing on the row saying a change happened at all.
 *
 * Raw number and ISO code rather than the `€`-prefixed price formatting elsewhere in IMS:
 * the currency is now known, and `event-summary.tsx`'s `formatPrice` hardcodes the euro. A
 * `$` or `kr` price printed as `€` would be a worse lie than an unformatted one. The code is
 * appended only when there is one — `price != null` is not a truthiness check, so a free
 * ticket renders `0`, and a currency-less proposal renders a bare number with no trailing
 * space.
 */
function TicketPrice({
  label,
  price,
  currency,
}: {
  /** Which link's price this is — one of the two `*_LINK_PRICE_LABEL`s above. */
  label: string;
  price: number;
  currency?: string | null;
}) {
  return (
    <p className="text-xs text-muted-foreground">
      {label}: {price}
      {currency ? ` ${currency}` : ""}
    </p>
  );
}

/**
 * A `venue` proposal's current location, when the published event has a map pin.
 *
 * ## What this replaces
 *
 * The `(empty)` half of the generic `old → new` line, for the one case where that word is
 * false. `old` is null because the published event has no venue RECORD — but "no venue
 * record" is not "no location": the event carries a Google Maps pin, so the curator is being
 * asked "is this the right venue?" with the one piece of evidence that answers it hidden.
 * On the owner's parks that read as
 *
 *     (empty)  →  Opposite, Av. de Francesc Ferrer i Guàrdia, 13, …
 *
 * for events that do have a pin. The arrow itself is true here (the event is at one place,
 * the page proposes another), so this keeps the arrow and the proposed value, and substitutes
 * only the half that lied.
 *
 * ## Why the pin is a link and not a summary
 *
 * The format varies on live data — some parks carry a short
 * `https://maps.google.com/?cid=…`, others a long `/maps/place/41°23'20.4"N…@lat,lng…` URL —
 * so nothing is read out of either. It is handed to Google as-is, which is also the only way
 * to answer "where is this, really" without inventing a coordinate parser that would be
 * wrong for one of the two shapes.
 *
 * ## Why the label says "current"
 *
 * The row shows two locations and only one of them is the event's. "Current location (map
 * pin)" names which is which, so the blue link text is not mistaken for a second rendering
 * of the proposed venue. "Map pin" rather than "venue", because it is not a venue: there is
 * no record behind it, which is the whole reason this proposal exists.
 *
 * ## What happens without a pin
 *
 * The caller does not render this at all (`isVenueLocation` requires a `maps_url`), and the
 * generic line keeps "(empty)". There, `old` really is everything known about the current
 * location, so "(empty)" is the honest word for it — see `ProposalRow`'s comment.
 *
 * Presentation only: nothing here is sent on accept or reject, which still carry the venue
 * id the curator picks below this line.
 */
function VenueComparison({
  proposal,
  mapsUrl,
}: {
  proposal: ProposedChange;
  /** The published event's own map link. Non-null: the caller checks before rendering. */
  mapsUrl: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      {/* Blue and underlined-on-hover, like every other link on this panel: it leaves
          IMS, so it has to look like the thing that leaves IMS. `noreferrer` without
          `noopener` is the house style for an outbound link here — see
          `cinemaContextRows`' Source row. */}
      <a
        href={mapsUrl}
        target="_blank"
        rel="noreferrer"
        className="text-blue-500 hover:underline"
      >
        Current location (map pin)
      </a>
      <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="font-medium text-foreground">{proposal.new}</span>
    </div>
  );
}

/**
 * Opens the proposed venue in a Google Maps search, in a new tab — see
 * `venueMapsSearchUrl`. Renders nothing when the proposal has no venue text.
 */
function VenueMapsSearch({
  venueText,
  city,
}: {
  venueText: string | null | undefined;
  city?: string;
}) {
  const href = venueMapsSearchUrl(venueText, city);
  if (!href) return null;
  return (
    <Button variant="outline" size="sm" className="w-fit" asChild>
      <a href={href} target="_blank" rel="noreferrer">
        <ExternalLink className="h-3.5 w-3.5" />
        Search on Google Maps
      </a>
    </Button>
  );
}

/**
 * One tick per performer, and the reason a refresh can delete one at all.
 *
 * ## Why this exists
 *
 * Accepting a `lineup` proposal used to be all-or-nothing: every name the model proposed
 * was added, and every removal it expressed was silently discarded. So a lineup that had
 * genuinely lost an act could never be corrected through a refresh — the curator's only
 * options were "add the new names and keep the stale one" or "reject the lot".
 *
 * ## The two defaults, and why they differ
 *
 * Additions arrive **ticked**, removals arrive **unticked** (product owner's ruling).
 * Adding a performer to a live event is additive and cheap to undo; removing one deletes
 * an act from an event people have already saved. So the destructive half is opt-in and
 * has to be clicked, once per name. That asymmetry is the design, not an inconsistency —
 * it is why "Accept ticked" on an untouched card does exactly what today's plain Accept
 * does, and nothing more.
 *
 * ## What gets sent
 *
 * Not what this list looks like. `buildRefreshDecisions` sends **one entry per delta**,
 * ticked or not, because tina treats a non-empty `lineup` list as exhaustive — an addition
 * missing from it is not applied. The unticked rows on screen are as load-bearing as the
 * ticked ones.
 *
 * `unchanged` names are rendered but never tickable: they are on both sides of the diff,
 * so there is no decision to make about them. They earn their line because "should this
 * removal go through" is much easier to answer next to the roster that would be left.
 */
function LineupDeltaList({
  deltas,
  unchanged,
  decision,
  onChange,
}: {
  deltas: LineupDelta[];
  unchanged: string[];
  decision: ProposalDecision;
  onChange: (partial: Partial<ProposalDecision>) => void;
}) {
  const ticked = deltas.filter((delta) => lineupTickAt(decision, delta));
  const additions = deltas.filter((delta) => delta.action === "add").length;
  const removals = deltas.length - additions;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">
          {deltas.length === 1 ? "1 change" : `${deltas.length} changes`}
        </span>
        <span className="text-xs text-muted-foreground">
          {[
            additions > 0 && `${additions} to add`,
            removals > 0 && `${removals} to remove`,
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </div>

      <ul className="flex flex-col gap-1">
        {deltas.map((delta) => {
          const key = lineupDeltaKey(delta);
          const isRemoval = delta.action === "remove";
          return (
            <li key={key}>
              {/* The whole row is the label, so the name itself is a click
                  target — these are short strings and a 16px checkbox is a
                  mean thing to ask a curator to hit once per performer. */}
              <label
                className={cn(
                  "flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1.5 text-sm",
                  isRemoval
                    ? "border-rose-500/30 bg-rose-500/5"
                    : "border-emerald-500/30 bg-emerald-500/5",
                )}
              >
                <Checkbox
                  // Verb plus name: "remove Yussef Dayes" is the sentence the
                  // tick actually means, and it is what a screen reader — and
                  // the tests — should hear.
                  aria-label={`${delta.action} ${delta.name}`}
                  checked={lineupTickAt(decision, delta)}
                  onCheckedChange={(value) =>
                    onChange({
                      lineupTicks: {
                        ...decision.lineupTicks,
                        [key]: value === true,
                      },
                    })
                  }
                />
                <span
                  aria-hidden
                  className={cn(
                    "font-mono text-sm",
                    isRemoval
                      ? "text-rose-600 dark:text-rose-400"
                      : "text-emerald-600 dark:text-emerald-400",
                  )}
                >
                  {isRemoval ? "−" : "+"}
                </span>
                <span className="flex-1 font-medium text-foreground">
                  {delta.name}
                </span>
                <span
                  className={cn(
                    "text-xs",
                    isRemoval
                      ? "text-rose-600 dark:text-rose-400"
                      : "text-muted-foreground",
                  )}
                >
                  {isRemoval ? "remove" : "add"}
                </span>
              </label>
            </li>
          );
        })}
      </ul>

      {unchanged.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Staying either way: {unchanged.join(", ")}
        </p>
      )}

      {/* Not a blocking gate — "accept, change nobody" is a legitimate (if
          unusual) answer, and the curator may have unticked everything on
          purpose. But it is indistinguishable from Reject in its effect, so it
          gets said out loud rather than submitted in silence. */}
      {decision.accept === true && ticked.length === 0 && (
        <p className="text-xs font-medium text-amber-700 dark:text-amber-300">
          Nothing is ticked — accepting will change no performers.
        </p>
      )}
    </div>
  );
}

/**
 * The current Mood poster beside the candidates the page offered (owner rulings
 * R36 and R76).
 *
 * The whole question on a poster proposal is "is the new one better", and it
 * cannot be answered from a URL diff — so this is the one proposal kind whose
 * evidence is the images themselves. Side by side, because a curator comparing
 * two posters one above the other is comparing a memory.
 *
 * The two halves are **fetched** differently on purpose and **zoom** the same
 * way. The current poster is already published and served from our own CDN, so
 * it needs no proxy; a candidate is a scraped artifact whose host may send
 * `Cross-Origin-Resource-Policy: same-origin`, so it goes through `GcsImage`'s
 * orchestrator proxy. That distinction is real and stays.
 *
 * What did not follow from it — and was wrong for a year of this component's
 * life — is that the current half rendered a bare `<img>` and so was the one
 * image on the screen a curator could not enlarge. `GcsImage` wraps every
 * candidate in `ZoomablePhoto`; both halves do now. The whole question here is
 * "is the new one better", asked of two 112px thumbnails, and answering it with
 * only one of them enlargeable is not a comparison. Not needing the proxy was
 * never a reason not to zoom: `ZoomablePhoto` takes a plain `url`.
 *
 * ## Why the labels sit where they do
 *
 * They used to be `figcaption`s **under** each half, and on the live park that
 * read as "PROPOSED" to the right of the current image and "CURRENT" beneath
 * it — a caption under a wrapped flex row does not visibly belong to anything.
 * Each label now sits directly **above** its own image, which is the one
 * placement that survives the wrap: in a narrow column the two halves stack,
 * and each still reads as its own label followed by its own picture. A missing
 * half keeps its label and says why in words, so the surviving image is never
 * the unlabelled one.
 *
 * No arrow between them, deliberately. One would read nicely — "this, becoming
 * that" — but it cannot be aligned against the middle of a column whose height
 * depends on a label, a thumbnail and a caption, and a visibly misaligned arrow
 * is worse than the left-to-right order on its own.
 *
 * ## Why the candidates are numbered
 *
 * A `poster` proposal's `new` is a candidate **position**, not a value — see
 * {@link posterCandidatePosition}. Numbering every candidate and marking the
 * one the model named is what makes that position mean something; printing the
 * bare `1` the wire carries did not.
 */
function PosterComparison({
  eventId,
  imageRefs,
  currentPosterUrl,
  proposedPosition,
}: PosterEvidence & {
  /** 1-based index into `imageRefs` of the candidate the model picked, if it named one. */
  proposedPosition: number | null;
}) {
  return (
    <div className="flex flex-wrap items-start gap-4">
      <figure className="flex flex-col gap-1">
        <PosterLabel>Current</PosterLabel>
        <CurrentPoster url={currentPosterUrl} />
      </figure>
      <figure className="flex min-w-0 flex-col gap-1">
        <PosterLabel>Proposed</PosterLabel>
        <div className="flex flex-wrap gap-2">
          {imageRefs.length > 0 ? (
            imageRefs.map((ref, index) => (
              <PosterCandidate
                key={ref}
                eventId={eventId}
                artifactRef={ref}
                position={index + 1}
                picked={proposedPosition === index + 1}
              />
            ))
          ) : (
            // A poster proposal with no stored candidate: the compare stage
            // read the page's image but the fetch stage stored none. Say so
            // rather than leaving a gap where an image should be.
            <ImageNotice>No proposed image stored</ImageNotice>
          )}
        </div>
      </figure>
    </div>
  );
}

/**
 * One candidate, numbered, and marked when the model named this position.
 *
 * The number is always shown, even for a single candidate: it is what turns the
 * proposal's `new: "1"` from a bare digit into a reference to something on the
 * screen. `picked` is only ever true when the model actually named a position —
 * a `new` that carries a URL instead leaves every candidate unmarked rather
 * than guessing at the first.
 */
function PosterCandidate({
  eventId,
  artifactRef,
  position,
  picked,
}: {
  eventId: string;
  artifactRef: string;
  position: number;
  picked: boolean;
}) {
  return (
    <span className="flex flex-col items-center gap-1">
      {/* `fallback`, not a bare `<GcsImage>`: with no `fallbackUrl` to try —
          `image_refs` carries refs only, never source URLs — a failed artifact
          fetch rendered nothing at all, so a 403 on the proxy looked exactly
          like a park that offered no image. R75 fixes the 403 itself; this is
          only about being able to tell the two apart. */}
      <GcsImage
        eventId={eventId}
        artifactRef={artifactRef}
        alt={`Proposed poster candidate ${position}`}
        className={cn(
          "h-28 w-28 rounded-md border object-cover",
          picked && "ring-2 ring-emerald-500/60",
        )}
        fallback={<ImageNotice>{IMAGE_UNAVAILABLE}</ImageNotice>}
      />
      <span className="text-[11px] text-muted-foreground">
        Candidate {position}
        {picked && (
          <span className="ml-1 font-medium text-emerald-700 dark:text-emerald-300">
            · the model&apos;s pick
          </span>
        )}
      </span>
    </span>
  );
}

/**
 * The published poster, zoomable, or the reason there is nothing to show.
 *
 * Two ways to have no current poster and one render for both: the field is
 * absent on a park written before R36, and null on an event whose art is still
 * a placeholder. A load error lands in the same place — a broken-image icon
 * beside a real candidate would read as "the current poster is broken", which
 * is a different and much more alarming claim than "there is not one".
 *
 * `ZoomablePhoto`, not a bare `<img>`: this is the half of the comparison a
 * curator most needs to enlarge, since it is the one they are being asked to
 * replace. It is passed the CDN URL directly — no `GcsImage`, so no orchestrator
 * proxy, which a published same-origin-friendly image has never needed. Keeping
 * the error fallback is why `ZoomablePhoto` forwards `onError` at all.
 */
function CurrentPoster({ url }: { url?: string | null }) {
  const [failed, setFailed] = useState(false);

  if (!url || failed) {
    return <ImageNotice>No current poster</ImageNotice>;
  }
  return (
    <ZoomablePhoto
      url={url}
      alt="Current event poster"
      className="h-28 w-28 rounded-md border object-cover"
      onError={() => setFailed(true)}
    />
  );
}

/** The side labels, above their image rather than under it — see `PosterComparison`. */
function PosterLabel({ children }: { children: ReactNode }) {
  return (
    <figcaption className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
      {children}
    </figcaption>
  );
}

/**
 * A dashed box the size of a thumbnail, saying in words why there is no image.
 *
 * One component for every such state on this panel — "no current poster", "no
 * proposed image stored", and a candidate whose fetch failed — because they all
 * have to be legible as *deliberate* rather than as a broken layout, and
 * because the difference between them is the sentence, not the styling.
 */
function ImageNotice({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        // `leading-tight`, because the longest of these sentences has to wrap
        // to five lines inside the 80px header-strip box without clipping.
        "flex h-28 w-28 items-center justify-center rounded-md border border-dashed px-2 text-center text-[11px] leading-tight text-muted-foreground",
        className,
      )}
    >
      {children}
    </span>
  );
}
